import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import EditHeader from "./coms/EditHeader";
import { useInjector } from "@bb-vendure/admin-ui/react";
import {
  NotificationService,
  ModalService,
  I18nService,
} from "@bb-vendure/admin-ui/core";
import {
  EmptyIcon,
  TablePaginationComponent,
  TableSearch,
} from "./TableSearch";
import Text from "./coms/Text";
import { CdsIcon, Link } from "@bb-vendure/admin-ui/react";
import { arrowIcon, trashIcon } from "@cds/core/icon";
import useGetCustomsClearanceFiles from "./graphql/get-customs-clearance-files";

const rows = (data: any) => {
  return data.getCustomsClearanceFiles.items.map((item: any) => {
    return item;
  });
};

type HeaderType = {
  label: string;
  index: number;
  show: boolean;
};

type HeaderData = {
  id: HeaderType;
  name: HeaderType;
  source: HeaderType;
  createdAt: HeaderType;
};

const defaultHeaderList: HeaderData = {
  id: {
    label: "ID",
    show: true,
    index: 0,
  },
  name: {
    label: "common.name",
    show: true,
    index: 1,
  },
  source: {
    label: "editor.image-src",
    index: 2,
    show: true,
  },
  createdAt: {
    label: "common.created-at",
    index: 3,
    show: true,
  },
};

const CustomsClearanceFileTable = forwardRef(
  (
    {
      id,
      removeCustomsClearanceFile,
    }: {
      id: string;
      removeCustomsClearanceFile: (fileId: string) => void;
      refresh: () => void;
    },
    ref
  ) => {
    const modalService = useInjector(ModalService);
    const translator = useInjector(I18nService);
    const [headerList, setHeaderList] =
      React.useState<HeaderData>(defaultHeaderList);
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [pagination, setPagination] = React.useState({
      take: 10,
      skip: 0,
      search: "",
    });
    const { data, loading, error, refetch } = useGetCustomsClearanceFiles<any>(
      id,
      pagination.take,
      pagination.skip
    );

    const headerCellStyle: React.CSSProperties = {
      fontSize: "12px",
      textTransform: "uppercase",
      fontWeight: 600,
    };

    function formatDate(v: string) {
      const date = new Date(v);

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based in JavaScript
      const year = String(date.getFullYear()).substr(2);

      return `${day}/${month}/${year}`;
    }

    useImperativeHandle(ref, () => ({
      refreshTable() {
        refetch({
          id: id,
          options: {
            take: pagination.take,
            skip: pagination.skip,
            sort: {
              createdAt: "DESC",
            },
          },
        });
      },
    }));

    React.useEffect(() => {
      refetch({
        id: id,
        options: {
          take: pagination.take,
          skip: pagination.skip,
          sort: {
            createdAt: "DESC",
          },
        },
      });
    }, [pagination]);

    React.useEffect(() => {
      const data = localStorage.getItem("customsClearanTable");
      if (data) {
        setHeaderList(JSON.parse(data));
      }
      setIsLoaded(true);
    }, []);

    React.useEffect(() => {
      if (isLoaded) {
        localStorage.setItem("customsClearanTable", JSON.stringify(headerList));
      }
    }, [headerList, isLoaded]);

    return (
      <div>
        <p
          style={{
            margin: "12px 0px 6px 0px",
          }}
        >
          {translator.translate("customs-clearance.files")}
        </p>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow key={"header-row"}>
              <TableCell width={50} padding="none" />

              {Object.entries(headerList).map(([key, value]) => {
                if (value.show)
                  return (
                    <TableCell key={key} style={headerCellStyle} align="left">
                      {translator.translate(value.label)}
                    </TableCell>
                  );
              })}
              <TableCell style={headerCellStyle} align="center">
                {translator.translate("common.action")}
              </TableCell>
              <TableCell>
                <EditHeader
                  setHeaderList={setHeaderList}
                  headerList={headerList}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableSearch
            searchTerm={(term) =>
              setPagination({ ...pagination, skip: 0, search: term })
            }
            span={
              Object.entries(headerList).reduce(
                (a, b) => (b[1].show === true ? a + 1 : a),
                0
              ) + 3
            }
          />
          {data && data.getCustomsClearanceFiles.items.length <= 0 && (
            <EmptyIcon
              span={Object.entries(headerList).reduce(
                (a, b) => (b[1].show === true ? a + 2 : a),
                0
              )}
              size="100px"
              label={translator.translate("common.no-results")}
            />
          )}
          <TableBody>
            {data &&
              rows(data).map((item) => {
                return (
                  <TableRow key={item.id}>
                    <TableCell width={50} padding="none" />

                    {Object.entries(headerList)
                      .sort((a, b) => a[1].index - b[1].index)
                      .map(([key, value], index) => {
                        if (value.show) {
                          switch (key) {
                            case "id":
                              return (
                                <TableCell key={index}>{item.id}</TableCell>
                              );
                            case "name":
                              return (
                                <TableCell key={index}>{item.name}</TableCell>
                              );
                            case "createdAt":
                              return (
                                <TableCell key={index}>
                                  <Text text={formatDate(item[key])} />
                                </TableCell>
                              );
                            case "source":
                              return (
                                <TableCell key={index}>
                                  <a
                                    className="button-ghost"
                                    href={item.source}
                                    target="_blank"
                                  >
                                    <CdsIcon
                                      icon={arrowIcon}
                                      direction="right"
                                    />
                                    Open
                                  </a>
                                </TableCell>
                              );

                            default:
                              return (
                                <TableCell key={index}>
                                  <Text text={String(item[key])} />
                                </TableCell>
                              );
                          }
                        }
                      })}
                    <TableCell align="center">
                      <div
                        style={{ marginLeft: "0.5rem", cursor: "pointer" }}
                        onClick={() => {
                          modalService
                            .dialog({
                              title: "Remove a customs clearance file",
                              body: `Are you sure you want to remove "${item.name}"?`,
                              buttons: [
                                { label: "Cancel", type: "secondary" },
                                {
                                  label: "Delete",
                                  type: "danger",
                                  returnValue: true,
                                },
                              ],
                            })
                            .subscribe((result) => {
                              if (result) {
                                removeCustomsClearanceFile(item.id);
                                //   removeCustomsClearanceFile({
                                //     id: customsClearance.id,
                                //     assetId: file.id,
                                //   }).then(() => {
                                //     notificationService.success("Deleted");
                                //     refetch({
                                //       id: id,
                                //     });
                                //   });
                              }
                            });
                        }}
                      >
                        <CdsIcon size="md" icon={trashIcon} status="danger" />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {data && (
          <TablePaginationComponent
            pagination={pagination}
            setPagination={setPagination}
            total={data.getCustomsClearanceFiles.totalItems}
          />
        )}
      </div>
    );
  }
);

export default CustomsClearanceFileTable;
